/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { Table, message, Modal, Tag } from "antd";
import { Link, useSearchParams } from 'react-router-dom';
import { insurancePlanService, insuranceCompanyService } from '../../../services/insuranceService';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import { formatCurrency } from '../../../lib/format';

const InsurancePlanList = () => {
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState(
    searchParams.get('company_id') ? parseInt(searchParams.get('company_id')) : null
  );
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await insuranceCompanyService.getAll({ per_page: 100 });
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  }, []);

  const fetchPlans = useCallback(async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await insurancePlanService.getAll({
        page,
        per_page: pageSize,
        search: searchTerm || undefined,
        company_id: companyFilter || undefined
      });

      setPlans((response.data || []).map(p => ({
        id: p.id,
        name: p.name,
        code: p.code,
        company_id: p.company_id,
        company_name: p.company?.name || 'N/A',
        coverage_percentage: p.coverage_percentage,
        max_annual_amount: p.max_annual_amount,
        description: p.description,
        created_at: p.created_at
      })));
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Error fetching plans:', error);
      message.error('Error al cargar planes');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, companyFilter]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    fetchPlans(pagination.current, pagination.pageSize);
  }, [fetchPlans, pagination.current, pagination.pageSize]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPlans(1, pagination.pageSize);
    setPagination({ ...pagination, current: 1 });
  };

  const handleDelete = async () => {
    if (!planToDelete) return;
    try {
      await insurancePlanService.delete(planToDelete.id);
      message.success('Plan eliminado correctamente');
      setDeleteModalVisible(false);
      setPlanToDelete(null);
      fetchPlans(pagination.current, pagination.pageSize);
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al eliminar plan';
      message.error(errorMsg);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleCompanyFilterChange = (companyId) => {
    setCompanyFilter(companyId ? parseInt(companyId) : null);
    setPagination({ ...pagination, current: 1 });
  };

  const columns = [
    {
      title: "Nombre",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <Link to={`/edit-insurance-plan?id=${record.id}`}>{text}</Link>
      )
    },
    {
      title: "Código",
      dataIndex: "code",
      sorter: (a, b) => a.code.localeCompare(b.code)
    },
    {
      title: "Aseguradora",
      dataIndex: "company_name",
      sorter: (a, b) => a.company_name.localeCompare(b.company_name)
    },
    {
      title: "Cobertura %",
      dataIndex: "coverage_percentage",
      render: (value) => value ? `${value}%` : 'N/A'
    },
    {
      title: "Monto Máximo Anual",
      dataIndex: "max_annual_amount",
      render: (value) => value ? formatCurrency(value) : 'N/A'
    },
    {
      title: "",
      dataIndex: "actions",
      render: (text, record) => (
        <div className="text-end">
          <div className="dropdown dropdown-action">
            <Link
              to="#"
              className="action-icon dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="fas fa-ellipsis-v" />
            </Link>
            <div className="dropdown-menu dropdown-menu-end">
              <Link className="dropdown-item" to={`/edit-insurance-plan?id=${record.id}`}>
                <i className="far fa-edit me-2" />
                Editar
              </Link>
              <button
                className="dropdown-item"
                onClick={() => {
                  setPlanToDelete(record);
                  setDeleteModalVisible(true);
                }}
              >
                <i className="far fa-trash-alt me-2" />
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <>
      <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">Planes de Seguro</h3>
                <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Planes de Seguro' }]} />
              </div>
              <div className="col-auto text-end float-end ms-auto">
                <Link to="/add-insurance-plan" className="btn btn-primary">
                  <i className="fas fa-plus me-2" />
                  Agregar Plan
                </Link>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title">Lista de Planes de Seguro</h5>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <form onSubmit={handleSearch}>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <button type="submit" className="btn btn-primary">
                            <i className="fas fa-search" />
                          </button>
                        </div>
                      </form>
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-select"
                        value={companyFilter || ''}
                        onChange={(e) => handleCompanyFilterChange(e.target.value)}
                      >
                        <option value="">Todas las aseguradoras</option>
                        {companies.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <Table
                    columns={columns}
                    dataSource={plans}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                      ...pagination,
                      total: total,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} registros`
                    }}
                    onChange={handleTableChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <Modal
            title="Confirmar Eliminación"
            open={deleteModalVisible}
            onOk={handleDelete}
            onCancel={() => {
              setDeleteModalVisible(false);
              setPlanToDelete(null);
            }}
            okText="Eliminar"
            okButtonProps={{ danger: true }}
          >
            <p>
              ¿Está seguro de eliminar el plan <strong>{planToDelete?.name}</strong>?
            </p>
      </Modal>
    </>
  );
};

export default InsurancePlanList;
